import React, { useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { TextField } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getStepStyles } from '../../../conversion-wizard-components/src/shared-styles/shared-content-style';
import { validateNumeric, validateRangeObj } from '../shared/validation-util';
import { duration2Input, input2Duration } from '../shared/util';

const ConversionWindowUnstyled = (props) => {
  const {
    classes,
    updateWindowProp,
    conversionWindowInMin,
    defaultValue,
    maxWindowInMin,
    minWindowInMin,
    hideHourMinute,
    label,
    logActivity,
  } = props;

  const i18n = useContext(I18nContext);
  const [timeObj, setTimeObj] = useState(duration2Input(_.isUndefined(conversionWindowInMin) ? defaultValue : conversionWindowInMin));
  const [shouldShowError, setShouldShowError] = useState(conversionWindowInMin === 0);


  const onChangeTimeInput = useCallback((event) => {
    const timeKey = event.target.title;
    const inputValue = event.target.value;
    logActivity.trace(`ConversionWindow: onChangeTimeInput: ${timeKey} ${inputValue}`);

    if (validateNumeric(inputValue) || inputValue.length === 0) {
      const timeValue = parseInt(inputValue, 10);
      const updatedObj = { ...timeObj, [timeKey]: timeValue };

      setTimeObj(updatedObj);

      const updatedTime = input2Duration(updatedObj);

      if (validateRangeObj(updatedObj) && updatedTime <= maxWindowInMin && updatedTime > minWindowInMin) {
        setShouldShowError(false);
        updateWindowProp(updatedTime, false);
      } else {
        setShouldShowError(true);
        updateWindowProp(undefined, true);
      }
    }
  }, [logActivity, timeObj, maxWindowInMin, minWindowInMin, updateWindowProp]);

  const HourAndMinutes = useMemo(() => {
    if (hideHourMinute) {
      return null;
    }

    return (
      <div>
        <div className="col-sm-2" id="hourAndMinutesContainer">
          <TextField
            ariaLabel="hour"
            title="hour"
            value={_.isNaN(timeObj.hour) ? '' : timeObj.hour}
            onChange={onChangeTimeInput}
            aria-labelledby={label}
          />
          <div>{i18n.getString(_TL_('Hour'))}</div>
        </div>
        <div className="col-sm-2">
          <TextField
            ariaLabel="min"
            title="min"
            value={_.isNaN(timeObj.min) ? '' : timeObj.min}
            onChange={onChangeTimeInput}
            aria-labelledby={label}
          />
          <div>{i18n.getString(_TL_('Min'))}</div>
        </div>
      </div>
    );
  }, [hideHourMinute, timeObj.hour, timeObj.min, onChangeTimeInput, label, i18n]);

  return (
    <div>
      <div className="row">
        <div className="row col-sm-8">
          <div className="col-sm-2">
            <TextField
              ariaLabel="day"
              title="day"
              value={_.isNaN(timeObj.day) ? '' : timeObj.day}
              onChange={onChangeTimeInput}
              aria-labelledby={label}
            />
            <div>{i18n.getString(_TL_('Day'))}</div>
          </div>
          {HourAndMinutes}
        </div>
      </div>

      {shouldShowError && (
        <div className="row" role="alert">
          <p className={classes.errorText}>
            {i18n.getString(_TL_('Enter a valid conversion period.'))}
          </p>
        </div>
      )}
    </div>
  );
};

ConversionWindowUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  updateWindowProp: PropTypes.func.isRequired,
  conversionWindowInMin: PropTypes.number,
  defaultValue: PropTypes.number.isRequired,
  maxWindowInMin: PropTypes.number.isRequired,
  minWindowInMin: PropTypes.number,
  hideHourMinute: PropTypes.bool,
  label: PropTypes.string,
  logActivity: PropTypes.func,
};

ConversionWindowUnstyled.defaultProps = {
  conversionWindowInMin: undefined,
  minWindowInMin: 0, // conversion window and viewthrough conversion window should larger than 0
  hideHourMinute: false,
  label: '',
  logActivity: {
    trace: _.noop,
    error: _.noop,
  },
};

export const ConversionWindow = withDefaultStyles(ConversionWindowUnstyled, getStepStyles);
